// Resumen de costes del presupuesto (formato nuevo, diseño A+).
//
// Pinta SOLO lo guardado por el motor único del backend
// (GET /presupuestos/{id}/costes): no calcula nada al vuelo. En el cálculo
// nuevo (costes_version >= 2) el backend recalcula solo en cada cambio: se
// enseña "Actualizado hace X" y un enlace pequeño "Forzar recálculo". Un
// presupuesto antiguo enseña sus cifras guardadas hasta que se pulsa
// "Recalcular costes".
//
// Dos vistas dentro de la misma tarjeta:
// - Por partida: importe, % sobre la venta y origen de cada coste, con su
//   desglose ampliable.
//   El responsable del montaje, si es un socio (sin coste), sale "0,00 € · socio".
// - Por menú: cada línea con su venta, su coste (ingredientes, sustitutos,
//   cocina, personal y material) y su margen, más los costes generales del
//   evento. Menús + generales = total de la tarjeta.
import React, { useEffect, useState } from 'react';
import { ChevronDown, ChevronRight, Plus, RefreshCw } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';

const MARGEN_MINIMO = 35;

// Detalle con `grupo` (Material y menaje: hoja de material, cada menú, lavado):
// en la tarjeta solo el total de cada grupo; el "+" abre todo el detalle.
// [{ grupo, importe, lineas }] en el orden en que llegan
const agruparDetalle = (detalle) => {
  const grupos = [];
  const porNombre = {};
  (detalle || []).forEach((d) => {
    const nombre = d.grupo || d.concepto;
    if (!porNombre[nombre]) {
      porNombre[nombre] = { grupo: nombre, importe: 0, lineas: [] };
      grupos.push(porNombre[nombre]);
    }
    porNombre[nombre].importe += Number(d.importe) || 0;
    porNombre[nombre].lineas.push(d);
  });
  return grupos;
};

const euros = (n) => new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR' }).format(Number(n) || 0);
const pct = (n) => (n === null || n === undefined || Number.isNaN(Number(n))
  ? '-'
  : `${Number(n).toLocaleString('es-ES', { minimumFractionDigits: 1, maximumFractionDigits: 1 })} %`);

const TONOS_ORIGEN = {
  real: 'bg-[#E3F3EC] text-[#0F5E43]',
  planificado: 'bg-blue-100 text-blue-800',
  estimado: 'bg-[#FDF0DC] text-[#92400E]',
  mixto: 'bg-[#EEF1EF] text-[#4A5A53]',
  anterior: 'bg-[#EEF1EF] text-[#4A5A53]',
};
const tonoOrigen = (origen) => TONOS_ORIGEN[origen] || 'bg-[#EEF1EF] text-[#4A5A53]';

const claseMargen = (margen) => {
  if (margen === null || margen === undefined) return 'bg-[#EEF1EF] text-[#4A5A53]';
  if (margen >= MARGEN_MINIMO) return 'bg-[#E3F3EC] text-[#0F5E43]';
  if (margen >= 20) return 'bg-[#FDF0DC] text-[#92400E]';
  return 'bg-[#FDE8E6] text-[#8A1C12]';
};

const PARTES_MENU = [
  { clave: 'ingredientes', etiqueta: 'Ingredientes' },
  { clave: 'sustitutos', etiqueta: 'Sustitutos de adaptaciones' },
  { clave: 'cocina', etiqueta: 'Personal de cocina' },
  { clave: 'personal', etiqueta: 'Personal del menú' },
  { clave: 'material', etiqueta: 'Material y menaje' },
];

const fechaCorta = (iso) => {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

// ISO guardado por el backend en UTC (a veces sin zona): se interpreta como UTC
const fechaUtc = (iso) => {
  if (!iso) return null;
  const texto = String(iso);
  const d = new Date(/[zZ]|[+-]\d{2}:?\d{2}$/.test(texto) ? texto : `${texto}Z`);
  return Number.isNaN(d.getTime()) ? null : d;
};

// "hace un momento", "hace 5 min", "hace 3 h", "hace 2 días"
export const haceCuanto = (iso, ahora = Date.now()) => {
  const d = fechaUtc(iso);
  if (!d) return null;
  const min = Math.floor((ahora - d.getTime()) / 60000);
  if (min < 1) return 'hace un momento';
  if (min < 60) return `hace ${min} min`;
  const h = Math.floor(min / 60);
  if (h < 24) return `hace ${h} h`;
  const dias = Math.floor(h / 24);
  return `hace ${dias} ${dias === 1 ? 'día' : 'días'}`;
};

const EnlaceForzar = ({ onRecalcular, recalculando }) => (
  <button
    type="button"
    onClick={onRecalcular}
    disabled={recalculando}
    className="text-xs font-semibold text-[#11704F] hover:underline disabled:opacity-60"
    data-testid="forzar-recalculo-costes"
    title="Los costes se recalculan solos al cambiar el presupuesto; esto los rehace ahora"
  >
    {recalculando ? 'Recalculando...' : 'Forzar recálculo'}
  </button>
);

const BotonRecalcular = ({ onRecalcular, recalculando }) => (
  <button
    type="button"
    onClick={onRecalcular}
    disabled={recalculando}
    className="inline-flex items-center gap-1.5 self-start px-3 py-1.5 rounded-lg border border-duetto-primary text-sm font-semibold text-[#11704F] hover:bg-[#E3F3EC] disabled:opacity-60"
    data-testid="recalcular-costes"
  >
    <RefreshCw className={`w-4 h-4 ${recalculando ? 'animate-spin' : ''}`} />
    {recalculando ? 'Recalculando...' : 'Recalcular costes'}
  </button>
);

const ResumenCostes = ({
  costes, cargando = false, error = null, recalculando = false, onRecalcular, hayPlatosBorrados = false,
}) => {
  const [vista, setVista] = useState('partidas');
  const [abiertas, setAbiertas] = useState({});
  // Ventana con todo el detalle de una partida agrupada ({ etiqueta, detalle })
  const [detalleModal, setDetalleModal] = useState(null);
  // Refresca el "Actualizado hace X" cada minuto
  const [, setTick] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setTick(n => n + 1), 60000);
    return () => clearInterval(t);
  }, []);
  const alternar = (clave) => setAbiertas(prev => ({ ...prev, [clave]: !prev[clave] }));

  const cabecera = (
    <div className="flex items-center justify-between gap-3 mb-1">
      <h2 className="text-base font-bold text-[#16221D]">Resumen de costes</h2>
      <span className="text-xs md:text-[11px] font-bold tracking-wider text-[#0F5E43] border border-dashed border-duetto-primary px-2 py-0.5 rounded-md">INTERNO</span>
    </div>
  );

  if (!costes || !costes.guardado) {
    return (
      <section className="bg-white border border-[#E1E6E3] rounded-xl p-4 sm:p-5 flex flex-col gap-3" data-testid="resumen-costes">
        {cabecera}
        {cargando ? (
          <p className="text-sm text-[#5B6B64]">Cargando costes...</p>
        ) : (
          <>
            <p className="text-sm text-[#92400E] bg-[#FDF0DC] rounded-lg px-3 py-2" data-testid="costes-sin-calcular">
              {error || 'Este presupuesto no tiene los costes calculados.'}
            </p>
            {onRecalcular && (costes?.automatico
              ? <EnlaceForzar onRecalcular={onRecalcular} recalculando={recalculando} />
              : <BotonRecalcular onRecalcular={onRecalcular} recalculando={recalculando} />)}
          </>
        )}
      </section>
    );
  }

  const ingresos = Number(costes.ingresos) || 0;
  const porVenta = (importe) => (ingresos > 0 ? (Number(importe) || 0) / ingresos * 100 : null);
  const filas = (costes.filas || []).filter(f => Math.abs(Number(f.importe) || 0) >= 0.005
    || ['responsable', 'personal_menu'].includes(f.clave));
  const porMenu = costes.por_menu || [];
  const generales = (costes.generales || []).filter(g => Math.abs(Number(g.importe) || 0) >= 0.005);
  const esNuevo = costes.metodo === 'nuevo';
  const automatico = !!costes.automatico;
  const fechaCalculo = fechaUtc(costes.calculado_en);
  const calculado = fechaCalculo ? fechaCorta(fechaCalculo.toISOString()) : null;
  const hace = haceCuanto(costes.calculado_en);

  const claseTab = (activa) => `px-3 py-1 rounded-md text-sm font-semibold ${activa
    ? 'bg-white text-[#16221D] shadow-sm'
    : 'text-[#5B6B64] hover:text-[#16221D]'}`;

  return (
    <section className="bg-white border border-[#E1E6E3] rounded-xl p-4 sm:p-5 flex flex-col gap-2" data-testid="resumen-costes">
      {cabecera}

      {esNuevo && (
        <div className="inline-flex self-start gap-1 p-1 rounded-lg bg-[#EEF1EF] mb-1" role="tablist">
          <button type="button" role="tab" aria-selected={vista === 'partidas'} className={claseTab(vista === 'partidas')}
            onClick={() => setVista('partidas')} data-testid="costes-vista-partidas">Por partida</button>
          <button type="button" role="tab" aria-selected={vista === 'menus'} className={claseTab(vista === 'menus')}
            onClick={() => setVista('menus')} data-testid="costes-vista-menus">Por menú</button>
        </div>
      )}

      <Dialog open={!!detalleModal} onOpenChange={(abierto) => { if (!abierto) setDetalleModal(null); }}>
        <DialogContent className="bg-white w-[calc(100vw-1rem)] max-w-lg max-h-[92vh] md:max-h-[85vh] overflow-y-auto p-4 md:p-6" data-testid="coste-detalle-modal">
          <DialogHeader>
            <DialogTitle>{detalleModal?.etiqueta}</DialogTitle>
            <DialogDescription>Detalle de cada artículo: cantidad, precio e importe.</DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-4">
            {agruparDetalle(detalleModal?.detalle).map(gr => (
              <div key={gr.grupo}>
                <div className="flex justify-between gap-3 text-sm font-semibold text-[#16221D] border-b border-[#E1E6E3] pb-1 mb-1">
                  <span className="min-w-0">{gr.grupo}</span>
                  <span className="tabular-nums whitespace-nowrap">{euros(gr.importe)}</span>
                </div>
                {gr.lineas.map((d, i) => (
                  <div key={`${gr.grupo}-${i}`} className="flex justify-between gap-3 text-xs text-[#4A5A53] pl-3 py-0.5">
                    <span className="min-w-0">{d.concepto}</span>
                    <span className="tabular-nums whitespace-nowrap">{euros(d.importe)}</span>
                  </div>
                ))}
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {vista === 'partidas' || !esNuevo ? (
        <div className="flex flex-col" data-testid="costes-por-partida">
          {filas.map((f) => {
            const detalle = f.detalle || [];
            const ampliable = detalle.length > 0 || !!f.nota;
            const abierta = !!abiertas[`p-${f.clave}`];
            const sinPersonal = ['responsable', 'personal_menu'].includes(f.clave) && Math.abs(Number(f.importe) || 0) < 0.005;
            return (
              <div key={f.clave} className="border-b border-[#EEF1EF] last:border-b-0" data-testid={`coste-fila-${f.clave}`}>
                <button
                  type="button"
                  onClick={() => ampliable && alternar(`p-${f.clave}`)}
                  className={`w-full grid grid-cols-[1fr_auto_auto] items-baseline gap-x-3 py-2 text-left text-sm ${ampliable ? 'cursor-pointer' : 'cursor-default'}`}
                  aria-expanded={ampliable ? abierta : undefined}
                >
                  <span className="flex items-start gap-1 min-w-0">
                    {ampliable
                      ? (abierta ? <ChevronDown className="w-4 h-4 mt-0.5 flex-shrink-0 text-[#5B6B64]" /> : <ChevronRight className="w-4 h-4 mt-0.5 flex-shrink-0 text-[#5B6B64]" />)
                      : <span className="w-4 flex-shrink-0" />}
                    <span className="min-w-0">
                      <span className="text-[#16221D]">{f.etiqueta}</span>
                      {f.origen_texto && (
                        <span className={`block w-fit mt-0.5 px-2 py-0.5 rounded-full text-xs md:text-[11px] font-bold ${sinPersonal ? tonoOrigen('estimado') : tonoOrigen(f.origen)}`}>
                          {sinPersonal && f.clave === 'personal_menu' ? 'Sin personal del menú' : f.origen_texto}
                        </span>
                      )}
                    </span>
                  </span>
                  <span className="tabular-nums whitespace-nowrap font-semibold text-[#16221D]" data-testid={`coste-importe-${f.clave}`}>
                    {euros(f.importe)}{f.socio ? ' · socio' : ''}
                  </span>
                  <span className="tabular-nums whitespace-nowrap text-xs text-[#5B6B64] w-14 text-right">{pct(porVenta(f.importe))}</span>
                </button>
                {abierta && detalle.some(d => d.grupo) && (
                  <div className="pl-5 pb-2 flex flex-col gap-1" data-testid={`coste-detalle-${f.clave}`}>
                    {agruparDetalle(detalle).map(gr => (
                      <div key={gr.grupo} className="flex justify-between gap-3 text-xs text-[#4A5A53]">
                        <span className="min-w-0">{gr.grupo}</span>
                        <span className="tabular-nums whitespace-nowrap">{euros(gr.importe)}</span>
                      </div>
                    ))}
                    <button
                      type="button"
                      onClick={() => setDetalleModal({ etiqueta: f.etiqueta, detalle })}
                      className="self-start inline-flex items-center gap-1 mt-1 min-h-[40px] md:min-h-0 text-sm md:text-xs font-semibold text-[#11704F] hover:underline"
                      data-testid={`coste-detalle-mas-${f.clave}`}
                      data-uso="costes.ver_detalle"
                    >
                      <Plus className="w-3.5 h-3.5" /> Ver detalle
                    </button>
                    {f.nota && <p className="text-xs text-[#5B6B64]">{f.nota}</p>}
                  </div>
                )}
                {abierta && !detalle.some(d => d.grupo) && (
                  <div className="pl-5 pb-2 flex flex-col gap-1" data-testid={`coste-detalle-${f.clave}`}>
                    {detalle.map((d, i) => (
                      <div key={`${f.clave}-${i}`} className="flex justify-between gap-3 text-xs text-[#4A5A53]">
                        <span className="min-w-0">
                          {d.concepto}
                          {d.nota && <span className="text-[#92400E]"> · {d.nota}</span>}
                        </span>
                        {/* Importes negativos (p. ej. bebida del lunch cubierta por la barra libre) en rojo y con signo */}
                        <span className={`tabular-nums whitespace-nowrap${(Number(d.importe) || 0) < 0 ? ' text-[#B42318]' : ''}`}>{euros(d.importe)}</span>
                      </div>
                    ))}
                    {f.nota && <p className="text-xs text-[#5B6B64]">{f.nota}</p>}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ) : (
        <div className="flex flex-col" data-testid="costes-por-menu">
          <div className="grid grid-cols-[1fr_auto_auto_auto] gap-x-3 pb-1 text-xs md:text-[11px] font-bold tracking-wide uppercase text-[#5B6B64] border-b border-[#E1E6E3]">
            <span>Línea</span><span className="text-right">Venta</span><span className="text-right">Coste</span><span className="text-right w-14">Margen</span>
          </div>
          {porMenu.map((m) => {
            const abierta = !!abiertas[`m-${m.clave}`];
            const partes = PARTES_MENU.filter(pm => Math.abs(Number(m[pm.clave]) || 0) >= 0.005);
            return (
              <div key={m.clave} className="border-b border-[#EEF1EF]" data-testid="coste-menu">
                <button
                  type="button"
                  onClick={() => partes.length > 0 && alternar(`m-${m.clave}`)}
                  className="w-full grid grid-cols-[1fr_auto_auto_auto] items-baseline gap-x-3 py-2 text-left text-sm"
                  aria-expanded={partes.length > 0 ? abierta : undefined}
                >
                  <span className="flex items-start gap-1 min-w-0">
                    {partes.length > 0
                      ? (abierta ? <ChevronDown className="w-4 h-4 mt-0.5 flex-shrink-0 text-[#5B6B64]" /> : <ChevronRight className="w-4 h-4 mt-0.5 flex-shrink-0 text-[#5B6B64]" />)
                      : <span className="w-4 flex-shrink-0" />}
                    <span className="min-w-0 text-[#16221D]">{m.concepto}</span>
                  </span>
                  <span className="tabular-nums whitespace-nowrap">{euros(m.venta)}</span>
                  <span className="tabular-nums whitespace-nowrap font-semibold">{euros(m.coste)}</span>
                  <span className="tabular-nums whitespace-nowrap text-xs text-right w-14">{pct(m.margen)}</span>
                </button>
                {abierta && (
                  <div className="pl-5 pb-2 flex flex-col gap-1">
                    {partes.map(pm => (
                      <div key={pm.clave} className="flex justify-between gap-3 text-xs text-[#4A5A53]">
                        <span>{pm.etiqueta}</span>
                        <span className="tabular-nums whitespace-nowrap">{euros(m[pm.clave])}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
          {generales.length > 0 && (
            <>
              <div className="pt-3 pb-1 text-xs md:text-[11px] font-bold tracking-wide uppercase text-[#5B6B64]">Costes generales del evento</div>
              {generales.map(g => (
                <div key={g.clave} className="flex justify-between gap-3 py-1 text-sm" data-testid={`coste-general-${g.clave}`}>
                  <span className="text-[#16221D]">{g.etiqueta}</span>
                  <span className="tabular-nums whitespace-nowrap font-semibold">{euros(g.importe)}</span>
                </div>
              ))}
            </>
          )}
        </div>
      )}

      <div className="flex justify-between items-baseline gap-3 text-sm font-bold border-t border-[#E1E6E3] pt-2">
        <span>Total costes</span>
        <span className="tabular-nums whitespace-nowrap">{euros(costes.coste_total)}</span>
      </div>
      <div className="flex justify-between items-baseline gap-3 text-sm text-[#5B6B64]">
        <span>Ingresos sin IVA</span>
        <span className="tabular-nums whitespace-nowrap">{euros(ingresos)}</span>
      </div>
      {(Number(costes.suplementos) > 0 || Number(costes.descuentos) > 0) && (
        <p className="text-xs text-[#5B6B64]">
          {Number(costes.suplementos) > 0 ? `Incluye suplementos por ${euros(costes.suplementos)}` : ''}
          {Number(costes.suplementos) > 0 && Number(costes.descuentos) > 0 ? '. ' : ''}
          {Number(costes.descuentos) > 0 ? `Descuentos aplicados: ${euros(costes.descuentos)}` : ''}
        </p>
      )}
      <div className={`flex justify-between items-baseline gap-3 rounded-lg px-3 py-2.5 mt-1 ${claseMargen(costes.margen)}`} data-testid="costes-beneficio">
        <span className="font-bold">Beneficio {euros(costes.beneficio)}</span>
        <span className="text-lg font-extrabold tabular-nums">{pct(costes.margen)}</span>
      </div>

      {!esNuevo && (
        <p className="text-xs text-[#5B6B64]" data-testid="costes-metodo-anterior">
          {costes.motivo_metodo_anterior
            || 'Cifras guardadas con el método anterior: se conservan aunque se guarde el presupuesto. Pulsa Recalcular costes para pasarlo al cálculo nuevo.'}
        </p>
      )}
      {hayPlatosBorrados && (
        <p className="text-xs text-[#8A1C12] bg-[#FDE8E6] rounded-lg px-2.5 py-2">
          Hay platos que ya no existen en el catálogo: su coste cuenta como 0 y el margen puede estar inflado.
        </p>
      )}
      {error && <p className="text-xs text-[#8A1C12]">{error}</p>}
      <div className="flex flex-wrap items-center justify-between gap-2 mt-1">
        {automatico ? (
          <span className="text-xs text-[#5B6B64]" data-testid="costes-actualizado" title={calculado ? `Calculado el ${calculado}` : undefined}>
            {hace ? `Actualizado ${hace}` : 'Se actualiza solo al cambiar el presupuesto'}
          </span>
        ) : (calculado ? <span className="text-xs text-[#5B6B64]">Calculado el {calculado}</span> : <span />)}
        {onRecalcular && (automatico
          ? <EnlaceForzar onRecalcular={onRecalcular} recalculando={recalculando} />
          : <BotonRecalcular onRecalcular={onRecalcular} recalculando={recalculando} />)}
      </div>
    </section>
  );
};

export default ResumenCostes;
